import { getTrackingId, LoginLayout } from "@euka-mfes/bark";
import { useNavigate } from "react-router-dom";
import NoAccessImage from "@assets/no-access.svg";
import { LearningClassEnums } from "@/ts/enums/LearningClassEnums";
import { Button, Flex, Heading, Text } from "@chakra-ui/react";
import { Trans } from "react-i18next";
import { useStudentData } from "@/context";
import { getConfig } from "@edx/frontend-platform";
import { GRADE_STATUS } from "@ts/enums/GradeStatus";
import { useEffect, useState } from "react";
import { isLearningDemoMode } from "@/demo/isLearningDemoMode";
import { mapGradeCodeToGradeParamId } from "@/utils/routeUtil";

const NoAccessContainer: React.FC = () => {
  const navigate = useNavigate();
  const { headerDetails, resetTermsDetails, resetLessonState } = useStudentData();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const demoMode = isLearningDemoMode();

  const config = getConfig();
  const logoutUrl = `${config.LMS_BASE_URL}/logout`;

  useEffect(() => {
    if (!demoMode || !headerDetails.grades.length) return;

    const fallbackGrade =
      headerDetails.grades.find(g => g.status === GRADE_STATUS.CURRENT) ??
      headerDetails.grades.find(g => g.status !== GRADE_STATUS.NOT_STARTED) ??
      headerDetails.grades[0];

    const fallbackGradeParam = fallbackGrade
      ? mapGradeCodeToGradeParamId(fallbackGrade.grade_code)
      : undefined;

    if (fallbackGradeParam) {
      navigate(`/learning/${fallbackGradeParam}`, { replace: true });
    }
  }, [demoMode, headerDetails.grades, navigate]);

  const handleButtonClick = () => {
    if (
      !headerDetails.grades.length ||
      (headerDetails.grades.length === 1 &&
        headerDetails.grades[0].status === GRADE_STATUS.NOT_STARTED)
    ) {
      setIsLoggingOut(true);

      // Clear any cached state
      try {
        sessionStorage.clear();
      } catch (e) {
        console.log(e);
      }

      window.location.replace(logoutUrl);
    } else {
      navigate("/learning/");
      resetTermsDetails();
      resetLessonState();
    }
  };

  return (
    <LoginLayout
      backgroundImg={NoAccessImage}
      trackingId={LearningClassEnums.NoAccessContainer}
    >
      <Flex flexDir="column" alignItems="center" textAlign="center" gap="6">
        <Heading
          data-tracking-id={getTrackingId(
            [LearningClassEnums.NoAccessContainer],
            "title"
          )}
          className="no-access-container-title"
          fontSize={{ base: "2xl", md: "3xl" }}
        >
          <Trans>You don't have access to this page</Trans>
        </Heading>
        <Text
          data-tracking-id={getTrackingId(
            [LearningClassEnums.NoAccessContainer],
            "subtitle"
          )}
          className="no-access-container-subtitle"
          fontSize={{ base: "sm", md: "md" }}
        >
          <Trans>Head back to your learning dashboard.</Trans>
        </Text>
        <Button
          data-tracking-id={getTrackingId(
            [LearningClassEnums.NoAccessContainer],
            "button"
          )}
          className="no-access-container-button"
          w="full"
          colorPalette="brand"
          onClick={handleButtonClick}
          loading={isLoggingOut}
        >
          <Trans>{headerDetails.grades.length ? "Home" : "Log out"}</Trans>
        </Button>
      </Flex>
    </LoginLayout>
  );
};

export default NoAccessContainer;
